import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useIsPhone } from "../../hooks/useIsPhone";
import type { CodeRequestDoc } from "../../services/codeRequestService";

interface CodeRequestsBannerProps {
  codeRequests: CodeRequestDoc[];
  /**
   * Called when the organizer clicks "Add User" on a code-request row.
   * Receives the email; parent opens the create dialog with email
   * prefilled.
   */
  onCreateFromRequest: (email: string) => void;
  /**
   * Called when the organizer clicks "Dismiss" on a code-request row.
   * Receives the whole doc (dismissal needs the doc id, not the email).
   */
  onDismissRequest: (request: CodeRequestDoc) => void;
}

/**
 * Renders a collapsible banner at the top of the Users page when there are
 * pending code-request rows. Lifted from the Attendees page (which used to
 * host this) because code requests come from people who haven't yet
 * authenticated and are therefore not in any specific event yet.
 *
 * The parent page owns the `codeRequests` state and refetch logic so it can
 * mark requests as fulfilled in lockstep with successful create flows.
 */
export function CodeRequestsBanner({
  codeRequests,
  onCreateFromRequest,
  onDismissRequest,
}: CodeRequestsBannerProps) {
  const [expanded, setExpanded] = useState(false);
  const isPhone = useIsPhone();

  if (codeRequests.length === 0) return null;

  return (
    <>
      <Alert
        severity="info"
        sx={{ mb: 2 }}
        data-testid="code-requests-banner"
        action={
          <Button
            size="small"
            color="inherit"
            onClick={() => setExpanded((prev) => !prev)}
            data-testid="code-requests-toggle"
            endIcon={
              expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />
            }
          >
            {codeRequests.length} pending code{" "}
            {codeRequests.length === 1 ? "request" : "requests"}
          </Button>
        }
      >
        People have requested invitation codes.
      </Alert>
      <Collapse in={expanded}>
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <List dense>
            {codeRequests.map((req) => {
              const actions = (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => onDismissRequest(req)}
                      data-testid={`code-request-dismiss-${req.email}`}
                    >
                      Dismiss
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onCreateFromRequest(req.email)}
                      data-testid={`code-request-add-user-${req.email}`}
                    >
                      Add User
                    </Button>
                  </Stack>
              );
              const text = (
                <ListItemText
                  primary={req.email}
                  secondary={
                    req.requestedAt?.toDate
                      ? `Requested ${req.requestedAt.toDate().toLocaleDateString()}`
                      : "Requested recently"
                  }
                  sx={isPhone ? { overflowWrap: "anywhere" } : undefined}
                />
              );
              // Phone: the buttons go under the email instead of over it.
              return isPhone ? (
                <ListItem
                  key={req.id}
                  data-testid={`code-request-row-${req.email}`}
                  sx={{ flexDirection: "column", alignItems: "flex-start", gap: 1 }}
                >
                  {text}
                  {actions}
                </ListItem>
              ) : (
                <ListItem
                  key={req.id}
                  data-testid={`code-request-row-${req.email}`}
                  secondaryAction={actions}
                >
                  {text}
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Collapse>
    </>
  );
}
